import { Gifts } from '.';
import { action, createActions, SB } from '@play-co/replicant';
import { ReplicantModuleState } from '../../utils';

export default (
  Gifts: Pick<Gifts, 'ruleset' | 'getters' | 'messages'>,
  { Inventory }: Gifts['$DEPENDENCIES'],
) =>
  createActions({} as SB.Schema<ReplicantModuleState<Gifts>>, {
    messages: Gifts.messages,
  })({
    /**
     * Send gift to target user over replicant message
     * It won't send U2U message! It will send only reward message
     */
    sendGiftsMessage: action((state, targetUserId: string, api) => {
      // Check if we can send to user
      if (!Gifts.getters(state).canSendGift(targetUserId, api.date.now()))
        throw new Error('Can`t send the gift');

      // Create record about sent reward
      state.gifts.sent[targetUserId] = { sentAt: api.date.now() };
      api.postMessage.receiveGift(targetUserId, {});

      // Self cleanup
      // We want to avoid crease user state
      // For this we will clean up old sent messages on every new message send
      const userIds = Object.keys(state.gifts.sent);
      const now = api.date.now();

      for (let index = 0; index < userIds.length; index++) {
        const id = userIds[index];
        const gift = state.gifts.sent[id];
        const cleanupDate = gift.sentAt + Gifts.ruleset.cleanupSendInterval;

        if (now >= cleanupDate) {
          delete state.gifts.sent[id];
        }
      }
    }),

    /**
     * Consume one gift from selected sender
     */
    consumeGift: action((state, senderId: string, api) => {
      // Sanitize input
      if (!senderId) throw new Error('Wrong senderId');
      if (!state.gifts.received[senderId]) throw new Error('Gift not found');

      // Inventory API
      const { addEnergy } = Inventory.modifiers(state);

      // How many energy we got and how many energy we have now
      const energyToAdd = state.gifts.received[senderId].energy;

      // Check if we won't reach energy gifts limit
      if (!Gifts.getters(state).canCollect(senderId, api.date.now()))
        throw new Error(
          'You can’t collect free energy when your energy balance is over ' +
            Gifts.ruleset.maxClaimEnergy,
        );

      // All good give energy from gift
      addEnergy(energyToAdd, api.date.now());

      // Remove gift record from state
      delete state.gifts.received[senderId];

      const analytics = {
        gift: 'energy',
        amount: energyToAdd,
        sender: senderId,
      };

      // Gift energy the player sent has been collected by the LINE friend
      // This event is sent as the sender’s inactive event
      api.sendAnalyticsEvents([
        {
          userId: senderId,
          eventType: 'GiftCollected',
          eventProperties: {
            ...analytics,
            sender: state.id, // Sender should be current user for this event
          },
        },
      ]);

      return analytics;
    }),

    /**
     * Consume all gift
     */
    consumeAllGifts: action((state, _: void, api) => {
      // Get senders ids
      const sendersIds = Object.keys(state.gifts.received);
      const analytics = [];

      // Move over all senders and add energy
      for (let index = 0; index < sendersIds.length; index++) {
        const senderId = sendersIds[index];
        const gift = state.gifts.received[senderId];
        const energyToAdd = gift.energy;

        // Skip energy that we can consume
        if (!Gifts.getters(state).canCollect(senderId, api.date.now()))
          continue;

        // All good give energy from gift
        Inventory.modifiers(state).addEnergy(energyToAdd, api.date.now());

        // Remove gift record from state
        delete state.gifts.received[senderId];

        // Collect analytics data for each successfully collected gift
        analytics.push({
          gift: 'energy',
          amount: energyToAdd,
          sender: senderId,
        });
      } // for

      // Collect analytics for sending it as batch
      const events = analytics.map((data) => ({
        userId: data.sender,
        eventType: 'GiftCollected',
        eventProperties: {
          ...data,
          sender: state.id, // Sender should be current user for this event
        },
      }));

      // Gift energy the player sent has been collected by the LINE friend
      // This event is sent as the sender’s inactive event
      api.sendAnalyticsEvents(events);

      // Return analytics for client
      return analytics;
    }),
  });
